import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { MatTable, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';

import { BreadcrumbService } from "../../../domain/services/breadcrumb/breadcrumb.service";
import { TaskModel } from "../../../domain/models/task.model";
import { TaskListDatasource } from "./task-list.datasource";
import { TaskService } from "../../../domain/services/task/task.service";
import { TaskStatusEnum } from "../../../domain/enums/task-status.enum";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { FormsModule } from "@angular/forms";
import { CommonModule, DatePipe } from "@angular/common";
import { Router, RouterModule } from "@angular/router";
import { DiscoveryService } from "../../../domain/services/discovery/discovery.service";
import { MatButtonModule } from "@angular/material/button";
import { MatInputModule } from "@angular/material/input";
import { MatOption, MatSelectModule } from "@angular/material/select";
import { StatusIndicatorComponent } from "../../../common/status-indicator/status-indicator.component";
import { RequestTypeIndicatorComponent } from "../../../common/request-type-indicator/request-type-indicator.component";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatNativeDateModule } from "@angular/material/core";
import { TaskType } from "../../../domain/enums/task-type.enum";
import { EnrollmentType, EnrollmentTypeStrMap } from "../../../domain/enums/enrollment-type.enum";
import {
  EnrollmentTypeIndicatorComponent
} from "../../../common/enrollment-type-indicator/enrollment-type-indicator.component";
import { Icons } from '../../../domain/enums/icons-enums';

@Component({
  selector: 'app-task-list',
  standalone: true,
  imports: [
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatButtonToggleModule,
    FormsModule,
    DatePipe,
    MatPaginatorModule,
    RouterModule,
    MatTableModule,
    MatInputModule,
    MatSortModule,
    MatSelectModule,
    MatOption,
    StatusIndicatorComponent,
    RequestTypeIndicatorComponent,
    CommonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    EnrollmentTypeIndicatorComponent,
  ],
  templateUrl: './task-list.component.html',
  styleUrl: './task-list.component.css'
})
export class TaskListComponent implements OnInit, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatTable) table!: MatTable<TaskModel>;

  dataSource = new TaskListDatasource([]);
  query = "";
  queryStatus: string = TaskStatusEnum.OPEN;
  queryStart!: Date;
  queryEnd!: Date;
  queryType = "";
  queryUserType = "";
  icons = Icons
  tasks: TaskModel[] = [];
  /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
  displayedColumns = ['id', 'dateReceived', 'type', 'description', 'status', 'actions'];
  protected readonly Date = Date;
  protected readonly TaskStatus = TaskStatusEnum;
  taskTypes = Object.values(TaskType);
  userTypes = Object.keys(EnrollmentType);

  protected readonly TaskType = TaskType;

  constructor(private readonly breadcrumbService: BreadcrumbService,
    private readonly service: TaskService,
    private readonly discovery: DiscoveryService,
    private readonly router: Router) {
  }

  /** Acknowledges the task and opens the case, which already exists (the pipeline creates it). */
  createCase(task: TaskModel): void {
    this.discovery.acknowledge(task.relatedEntityId).subscribe({
      next: () => this.router.navigate(['/app/discovery', task.relatedEntityId]),
      error: () => this.router.navigate(['/app/discovery', task.relatedEntityId]),
    });
  }

  ngOnInit(): void {
    this.breadcrumbService.updateBreadcrumbs([{
      icon: "fact_check",
      url: "/tasks",
      label: "Tasks"
    }]);
  }

  ngAfterViewInit(): void {
    this.service.getAll().subscribe(value => {
      this.tasks = value;
      this.rebuildDataSource(this.applyStatusFilter(value));
    });
  }

  rebuildDataSource(data: TaskModel[]) {
    this.dataSource = new TaskListDatasource(data);
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
    this.table.dataSource = this.dataSource;
  }

  onStatusFilter() {
    const data = this.applyStatusFilter(this.tasks);
    this.rebuildDataSource(data);
    this.query = "";
  }

  onFilter() {
    let data = this.applyAllFilters(this.tasks);
    this.rebuildDataSource(data);
    this.paginator.pageIndex = 0;
    this.query = "";
  }

  applyAllFilters(data: TaskModel[]): TaskModel[] {
    return this.applyTypeFilter(
      this.applyUserTypeFilter(
        this.applyDateFilter(
          this.applyStatusFilter(data)
        )));
  }

  applyStatusFilter(data: TaskModel[]) {
    if (data && data.length > 0
      && this.queryStatus
      && this.queryStatus.length > 0
      && this.queryStatus !== "all") {
      return data.filter(value => this.swapLockedStatus(value.status) === this.queryStatus);
    } else {
      return data;
    }
  }


  applyDateFilter(data: TaskModel[]) {
    if (data && data.length > 0 && this.queryStart && this.queryEnd) {
      return data.filter(value => {
        const time = new Date(value.dateReceived).getTime();
        return time >= this.queryStart.getTime() && time <= this.queryEnd.getTime()
      });
    } else {
      return data;
    }
  }

  applyUserTypeFilter(data: TaskModel[]) {
    if (data && data.length > 0 && this.queryUserType) {
      const selectedType = EnrollmentTypeStrMap.get(this.queryUserType);
      return data.filter(value => selectedType?.toString() ? value.description.includes(selectedType?.toString()) : false);
    } else {
      return data;
    }
  }

  applyTypeFilter(data: TaskModel[]) {
    if (data && data.length > 0 && this.queryType) {
      return data.filter(value => value.type === this.queryType);
    } else {
      return data;
    }
  }

  swapLockedStatus(status: string) {
    if (status == TaskStatusEnum.LOCKED) {
      return TaskStatusEnum.OPEN;
    }
    return status;
  }

  onSearch() {
    this.paginator.pageIndex = 0; //reset paginator on search
    if (this.query.length > 2) {
      const data = this.tasks.filter(value =>
        value.status.toLowerCase().includes(this.query.toLowerCase())
        || value.type.toLowerCase().includes(this.query.toLowerCase())
        || value.description.toLowerCase().includes(this.query.toLowerCase()));
      this.rebuildDataSource(this.applyStatusFilter(data));
    } else if (this.query.length == 0) {
      this.rebuildDataSource(this.applyStatusFilter(this.tasks));
    }
  }

  clearSerch() {
    this.query = '';
    this.onSearch();
  }


  protected readonly TaskStatusEnum = TaskStatusEnum;
  protected readonly EnrollmentTypeStrMap = EnrollmentTypeStrMap;
}
